import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { DagNodeData } from '../../utils/dag-layout';

const statusColors: Record<string, { border: string; bg: string; text: string }> = {
  HEALTHY: { border: '#4caf50', bg: '#e8f5e9', text: '#2e7d32' },
  DEGRADED: { border: '#ff9800', bg: '#fff3e0', text: '#e65100' },
  UNHEALTHY: { border: '#f44336', bg: '#ffebee', text: '#c62828' },
  UNREGISTERED: { border: '#b0bec5', bg: '#eceff1', text: '#78909c' },
};

const statusIcons: Record<string, string> = {
  HEALTHY: '●',
  DEGRADED: '⚠',
  UNHEALTHY: '✕',
  UNREGISTERED: '?',
};

function formatHeartbeatAge(heartbeatTime: string): string {
  if (!heartbeatTime) return 'unknown';
  const diff = Date.now() - new Date(heartbeatTime).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s ago`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
}

export const DagNodeComponent = memo(({ data }: { data: DagNodeData }) => {
  const colors = statusColors[data.aggregateStatus] ?? statusColors.UNHEALTHY;
  const latestHeartbeat = data.seeders
    .map((s) => s.heartbeatTime)
    .filter(Boolean)
    .sort()
    .reverse()[0];

  return (
    <div
      style={{
        background: 'white',
        border: `2px solid ${colors.border}`,
        borderRadius: '10px',
        padding: '12px 18px',
        minWidth: '220px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      <Handle type='target' position={Position.Top} style={{ visibility: 'hidden' }} />

      {data.hasUnhealthyUpstream && data.aggregateStatus === 'HEALTHY' && (
        <div
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            background: '#ff9800',
            color: 'white',
            borderRadius: '8px',
            padding: '1px 6px',
            fontSize: '9px',
            fontWeight: 'bold',
          }}
        >
          upstream ⚠
        </div>
      )}

      {data.aggregateStatus !== 'HEALTHY' && data.aggregateStatus !== 'UNREGISTERED' && (
        <div
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            background: colors.border,
            color: 'white',
            borderRadius: '50%',
            width: 18,
            height: 18,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
          }}
        >
          !
        </div>
      )}

      <div style={{ fontWeight: 700, fontSize: '13px', color: '#1a1a1a' }}>{data.label}</div>

      <div
        style={{
          fontSize: '11px',
          color: '#888',
          marginTop: '3px',
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
        }}
      >
        <span>
          {data.totalCount} seeder{data.totalCount !== 1 ? 's' : ''}
        </span>
        <span>·</span>
        <span>
          {data.dependencyCount} dep{data.dependencyCount !== 1 ? 's' : ''}
        </span>
      </div>

      {data.totalCount > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '5px',
            fontSize: '10px',
          }}
        >
          {data.healthyCount > 0 && <span style={{ color: '#4caf50', fontWeight: 600 }}>● {data.healthyCount}</span>}
          {data.unhealthyCount > 0 && (
            <span style={{ color: '#f44336', fontWeight: 600 }}>✕ {data.unhealthyCount}</span>
          )}
        </div>
      )}

      <div
        style={{
          fontSize: '10px',
          color: colors.text,
          marginTop: '4px',
          fontWeight: 500,
        }}
      >
        {statusIcons[data.aggregateStatus]} {data.aggregateStatus}
        {latestHeartbeat ? ` · ${formatHeartbeatAge(latestHeartbeat)}` : ''}
      </div>

      <Handle type='source' position={Position.Bottom} style={{ visibility: 'hidden' }} />
    </div>
  );
});

DagNodeComponent.displayName = 'DagNodeComponent';
